import { fetchcambioDeclaveSistema, fetchcambioDeestadoUsuario, fetchCrearUsuario, fetchEditarUsuario, obtener_Administradores, obtenerDetallesAdministrador, recuperar_Contraseña, validarSiexisteUsuario } from "./Api_Admins_Providers";

/**
 * Obtiene la información del administrador por su ID.
 *
 * @param {number} id - El ID del administrador.
 * @returns {Function} Una función thunk que retorna una Promesa con los detalles del administrador.
 */
export const obtenerAdministrador = (id) => {
    return async () => {
        try {
            // Llama a la API para obtener los detalles del administrador usando su ID
            const resultado = await obtenerDetallesAdministrador({ id });

            // Guard clause: si no hay datos, retorna null
            if (!resultado?.data?.data) return null;

            // Devuelve los datos del primer administrador encontrado
            return resultado.data.data["0"];
        } catch (error) {
            console.error("Error al obtener el administrador:", error);
            return null; // Retorna null en caso de error
        }
    };
};

/**
 * Obtiene la lista de administradores.
 *
 * @returns {Function} Una función thunk que retorna una Promesa con los datos de los administradores.
 */
export const obtenerAdministradores = () => {
    return async () => {
        try {
            // Llama a la API para obtener la lista de administradores
            const resultado = await obtener_Administradores();

            // Guard clause: si no hay datos, retorna un array vacío
            if (!resultado?.data?.data) return [];

            // Retorna los datos de los administradores
            return resultado.data.data;
        } catch (error) {
            console.error("Error al obtener los administradores:", error);
            return []; // Retorna un array vacío en caso de error
        }
    };
};


/**
 * Crea un nuevo usuario utilizando la API.
 *
 * @param {Object} formData - Datos del formulario para crear el usuario.
 * @returns {Function} Una función thunk que retorna una Promesa con los datos del usuario creado.
 */
export const crearUsuario = (formData) => {
    return async () => {
        try {
            // Llama a la API para crear un nuevo usuario con los datos proporcionados
            const resultado = await fetchCrearUsuario({ formData });

            // Si el resultado no contiene datos válidos, retorna un array vacío
            if (!resultado?.data?.data) return [];

            // Retorna los datos del usuario creado
            return resultado.data.data;
        } catch (error) {
            console.error("Error al crear el usuario:", error);
            return []; // Retorna un array vacío en caso de error
        }
    };
};



/**
 * Valida si un usuario existe en la base de datos utilizando su correo electrónico.
 *
 * @param {string} email - Correo electrónico del usuario a validar.
 * @returns {Function} Función thunk que retorna una Promesa con los datos del usuario si existe, o un array vacío si no existe o hay un error.
 */
export const ValidarUsuario = (email) => {
    return async () => {
        try {
            // Llama a la API para verificar si el usuario existe utilizando el correo electrónico proporcionado.
            const resultado = await validarSiexisteUsuario({ email });

            // Retorna los datos del usuario si existe, o un array vacío si no hay datos válidos.
            return resultado?.data?.data || [];
        } catch (error) {
            // Captura y registra cualquier error ocurrido durante la solicitud.
            console.error("Error al validar el usuario:", error);

            // Retorna un array vacío en caso de error para evitar fallos en el flujo de la aplicación.
            return [];
        }
    };
};

/**
 * Solicita la recuperación de contraseña para un usuario utilizando su correo electrónico.
 *
 * @param {string} email - Correo electrónico del usuario para la recuperación de contraseña.
 * @returns {Function} Función thunk que retorna una Promesa con los datos de recuperación o un array vacío si hay un error.
 */
export const recuperarContraseña = (email) => {
    return async () => {
        try {
            // Llama a la API para iniciar el proceso de recuperación de contraseña utilizando el correo electrónico proporcionado.
            const resultado = await recuperar_Contraseña({ email });

            // Retorna los datos de la respuesta de la API, o un array vacío si no hay datos válidos.
            return resultado?.data?.data || [];
        } catch (error) {
            // Captura y registra cualquier error ocurrido durante la solicitud.
            console.error("Error al solicitar la recuperación de contraseña:", error);

            // Retorna un array vacío en caso de error para manejar el fallo de manera segura.
            return [];
        }
    };
};

/**
 * Edita los datos de un usuario existente utilizando los datos proporcionados en el formulario.
 *
 * @param {Object} formData - Datos del formulario con la información actualizada del usuario.
 * @returns {Function} Función thunk que retorna una Promesa con los datos del usuario editado, o un array vacío si hay un error.
 */
export const editarUsuario = (formData) => {
    return async () => {
        try {
            // Llama a la API para editar el usuario con los datos proporcionados en el formulario.
            const resultado = await fetchEditarUsuario({ formData });

            // Si el resultado no contiene datos válidos, retorna un array vacío.
            if (!resultado?.data?.data) return [];

            // Retorna los datos del usuario editado.
            return resultado.data.data;
        } catch (error) {
            // Captura y registra cualquier error ocurrido durante la solicitud.
            console.error("Error al editar el usuario:", error);

            // Retorna un array vacío en caso de error para evitar interrupciones en el flujo de la aplicación.
            return [];
        }
    };
};




/**
 * Cambia el estado de un usuario (activo/inactivo) en la base de datos.
 *
 * @param {number} id - ID del usuario cuyo estado será modificado.
 * @param {boolean} estado - Nuevo estado del usuario (true para activo, false para inactivo).
 * @returns {Function} Función thunk que retorna una Promesa con los datos actualizados del usuario, o un array vacío si hay un error.
 */
export const cambioDeestadoUsuario = (id, estado) => {
    return async () => {
        try {
            // Llama a la API para cambiar el estado del usuario con los datos proporcionados.
            const resultado = await fetchcambioDeestadoUsuario({ id, estado });

            // Si el resultado no contiene datos válidos, retorna un array vacío.
            if (!resultado?.data?.data) return [];

            // Retorna los datos del usuario con el estado actualizado.
            return resultado.data.data;
        } catch (error) {
            // Captura y registra cualquier error ocurrido durante la solicitud.
            console.error("Error al cambiar el estado del usuario:", error);

            // Retorna un array vacío en caso de error para evitar interrupciones en el flujo de la aplicación.
            return [];
        }
    };
};





/**
 * Cambia la clave del sistema para el usuario autenticado.
 *
 * @param {string} password - Nueva clave para el usuario.
 * @returns {Function} Función thunk que retorna una Promesa con los datos del usuario actualizados, o un array vacío si hay un error.
 */
export const cambioDeclaveSistema = (password) => {
    return async (dispatch, getState) => {
        // Obtiene el ID del usuario autenticado desde el estado global.
        const { id_admin } = getState().auth;

        try {
            // Llama a la API para cambiar la clave del usuario autenticado.
            const resultado = await fetchcambioDeclaveSistema({ id: id_admin, password });

            // Verifica si la respuesta contiene datos válidos, de lo contrario retorna un array vacío.
            if (!resultado?.data?.data) return [];

            // Retorna los datos del usuario con la clave actualizada.
            return resultado.data.data;
        } catch (error) {
            // Captura y registra cualquier error ocurrido durante la solicitud.
            console.error("Error al cambiar la clave del sistema del usuario:", error);

            // Retorna un array vacío en caso de error para evitar interrupciones en el flujo de la aplicación.
            return [];
        }
    };
};
